// src/data/projectsData.js
export const GCS_BASE = "https://storage.googleapis.com/my-portfolio-media";

export function isVideoSrc(src) {
  const s = (src || "").toLowerCase();
  return s.endsWith(".mp4") || s.endsWith(".webm") || s.endsWith(".mov");
}

export function resolveGcsMedia(media) {
  if (!media) return "";
  if (/^https?:\/\//i.test(media)) return media;
  const clean = String(media).replace(/^\/+/, "");
  return `${GCS_BASE}/${clean}`;
}

export const projects = [
  {
    key: "hydromind",
    title: "HydroMind",
    subtitle: "Automated hydroponics control system",
    tag: "Smart Automation",
    audience: "Home growers & small farms",
    desc:
      "A control dashboard that monitors sensors and automates dosing/pumps so plants stay healthy with minimal manual work.",
    highlights: [
      "Live sensor monitoring (pH, nutrients, temperature)",
      "Automated dosing schedules + safety checks",
      "Hardware + software built as a complete product",
    ],
    stack: ["React", "Flask", "MySQL", "Cloud Run", "Cloud SQL", "KiCad"],
    media: `${GCS_BASE}/hydromind.mp4`,

    // ✅ Project page content (focus on what the product/site does)
    story: {
      summary:
        "A full monitoring + automation dashboard for hydroponics: live readings, dosing actions, schedules, and history in one place.",
      whoFor: [
        "Home growers who want a simple, guided system",
        "Small farms that need repeatable and trackable grow cycles",
      ],
      flow: [
        {
          title: "Live monitoring",
          detail:
            "The dashboard shows real-time pH/EC/temperature and recent trends so you always know the system state.",
        },
        {
          title: "Targets & schedules",
          detail:
            "Users configure target ranges and dosing schedules (or presets) to define what “healthy” means for their grow.",
        },
        {
          title: "Automatic actions",
          detail:
            "When readings drift, dosing actions can be triggered and logged with precise amounts and timestamps.",
        },
        {
          title: "History & logs",
          detail:
            "Charts and action logs help diagnose issues, confirm changes, and understand plant performance over time.",
        },
        {
          title: "Safety rules",
          detail:
            "Guardrails reduce risk (limits, cooldowns, and validation) so the system stays stable and predictable.",
        },
      ],
      features: [
        "Live sensor tiles + historical charts",
        "Dosing schedules/presets with visibility into what’s next",
        "Action logs (what happened, when, how much)",
        "Settings for targets, calibration, and system configuration",
      ],
      // architecture: [
      //   "Edge device/sensors send readings to the backend API",
      //   "API validates and stores readings in the database",
      //   "Dashboard fetches and visualizes real-time + historical data",
      //   "Automation logic triggers dosing presets and records actions",
      //   "Media/assets stored in cloud storage where needed",
      // ],
      results: [
        "More consistent monitoring with traceable history",
        "Less manual effort while keeping controls visible and safe",
      ],
    },
  },

  {
    key: "sahar-watches",
    title: "Sahar Watches",
    subtitle: "Luxury watch marketplace + in-store verification workflow",
    tag: "Marketplace Ops",
    audience: "Watch buyers, sellers, and store admin team",
    desc:
      "A platform where customers submit watches to sell, and the store manages review appointments, verification, and sales — with a full internal portal for inventory, clients, and the public showroom.",
    highlights: [
      "Customer sell submissions → admin review → in-store appointment flow",
      "Internal inventory tracking (in-stock, reserved, sold, incoming requests)",
      "Admin-controlled public showroom with albums and listings",
    ],
    stack: ["React", "Flask", "MySQL", "Google Cloud Storage"],
    media: `${GCS_BASE}/sahar-watches.mp4`,
  
    story: {
      summary:
        "A two-sided watch platform: customers submit watches to sell, while admins run the full operational workflow — screening submissions, booking verification appointments, moving inventory through statuses, and managing the public showroom listings and albums.",
      whoFor: [
        "Sellers submitting watches online",
        "Buyers browsing the public showroom",
        "Admins managing operations, inventory, and appointments",
      ],
      flow: [
        {
          title: "Customer submission (sell a watch)",
          detail:
            "Customers upload their watch details and photos through the website, creating an incoming request for review.",
        },
        {
          title: "Admin screening & triage",
          detail:
            "Admins review submissions, approve/decline, request more info, and prepare the next step for in-store verification.",
        },
        {
          title: "Appointment booking",
          detail:
            "Admins schedule an in-store meeting with the customer to inspect the watch, verify condition/authenticity, and confirm the sale process.",
        },
        {
          title: "Verification → inventory entry",
          detail:
            "After review, the watch is converted into inventory and tracked across statuses like incoming, in-stock, reserved, and sold.",
        },
        {
          title: "Public showroom management",
          detail:
            "Admins publish watches to the public showroom, manage pricing/specs, and maintain premium photo albums for each listing.",
        },
      ],
      features: [
        "Public sell submission form (watch details + photo upload)",
        "Admin review queue (approve/decline/request info)",
        "Appointments calendar + schedule view",
        "Customer list with notes and interaction history",
        "Inventory dashboard with status workflow (incoming/in-stock/reserved/sold)",
        "Public showroom CMS (publish/unpublish, edit listings, manage albums)",
        "Watch gallery with fast, organized media hosting",
      ],
      // architecture: [
      //   "React front-end for both the public site and internal admin portal",
      //   "Flask API for submissions, workflow actions, appointments, and inventory operations",
      //   "MySQL for users, submissions, appointments, inventory, and status history",
      //   "Google Cloud Storage for watch photos and album media (reliable + fast delivery)",
      // ],
      results: [
        "Clear operational workflow from online submission to in-store sale",
        "Centralized control of inventory, appointments, and customer interactions",
        "Premium, consistent showroom presentation with organized media",
      ],
    },
  },  

  {
    key: "ubic",
    title: "Construction Contractor",
    subtitle: "Lead-generation website for a contractor",
    tag: "Business Website",
    audience: "Local service business - Private Customer",
    desc:
      "A clean website that builds trust, showcases work, and makes it easy for customers to get in touch.",
    highlights: [
      "Service pages + project gallery",
      "Fast loading, mobile-friendly layout",
      "Simple contact flow for leads",
    ],
    stack: ["React", "Apps Script", "Google Cloud Storage"],
    media: `${GCS_BASE}/ubic.mp4`,
    story: {
      summary:
        "A modern business website designed to convert: clear services, proof of work, and a frictionless contact flow.",
      whoFor: ["Homeowners", "Prospective clients", "Business owner"],
      flow: [
        {
          title: "Discover services",
          detail:
            "Visitors quickly understand what the contractor offers and what outcomes to expect.",
        },
        {
          title: "See proof of work",
          detail:
            "A clean gallery highlights projects and builds trust through real examples.",
        },
        {
          title: "Fast contact flow",
          detail:
            "Clear call-to-actions guide users to request a quote without friction.",
        },
        {
          title: "Mobile-first experience",
          detail:
            "Layouts stay readable and quick on phones, where most leads come from.",
        },
      ],
      features: [
        "Services pages and trust-focused layout",
        "Project gallery",
        "Simple contact experience (lead capture)",
        "Fast performance and responsive design",
      ],
      // architecture: [
      //   "React site structure",
      //   "Apps Script for lightweight backend/form handling (when needed)",
      //   "Cloud storage for media assets",
      // ],
      results: [
        "A modern online presence that builds trust",
        "A simpler path from visit → inquiry",
      ],
    },
  },

  {
    key: "needle",
    title: "Needle",
    subtitle: "Inventory & sales system for watch dealers",
    tag: "Internal Tool",
    audience: "Watch Dealers / back office",
    desc:
      "A streamlined system that tracks purchases, sales, and watch photos to keep operations organized.",
    highlights: [
      "Watch albums and detailed item pages",
      "Purchases & sales history tracking",
      "Import/export with clean templates",
    ],
    stack: ["React", "Flask", "MySQL", "Cloud Run", "Cloud SQL", "GCS"],
    media: `${GCS_BASE}/needle.mp4`,
    story: {
      summary:
        "A dealer-grade internal tool that tracks inventory from purchase to sale, with fast media handling and clean imports/exports.",
      whoFor: ["Dealers", "Staff", "Management"],
      flow: [
        {
          title: "Create or import inventory",
          detail:
            "Add watches manually or import using templates to keep data consistent and fast to manage.",
        },
        {
          title: "Organize photos & details",
          detail:
            "Each watch has a clean page with photos, specs, and status for quick access.",
        },
        {
          title: "Track lifecycle",
          detail:
            "Purchases → inventory → reservation → sale, with clear history and accountability.",
        },
        {
          title: "Export/report",
          detail:
            "Generate clean exports for sharing, auditing, or internal reporting workflows.",
        },
      ],
      features: [
        "Inventory with watch pages and photo albums",
        "Purchases and sales history tracking",
        "Import/export templates",
        "Cloud-hosted media for reliable performance",
      ],
      // architecture: [
      //   "React UI for operations workflows",
      //   "Flask API + Cloud SQL for persistent business data",
      //   "Cloud Run for scalable hosting",
      //   "Cloud Storage for images and generated artifacts",
      // ],
      results: [
        "Reduced manual overhead for inventory operations",
        "More consistent data and faster workflows for staff",
      ],
    },
  },

  {
    key: "inspection",
    title: "Inspection Platform",
    subtitle: "Photo-to-report workflow for inspectors",
    tag: "Internal Tool",
    audience: "Inspection company - Private Customer",
    desc:
      "An internal tool that helps inspectors upload photos, apply standardized notes, and generate professional reports.",
    highlights: [
      "Structured photo upload & labeling flow",
      "Approval-ready reporting workflow",
      "PDF generation stored in cloud storage",
    ],
    stack: ["React", "Flask", "MySQL", "Cloud Run", "Cloud SQL", "GCS"],
    media: `${GCS_BASE}/inspections.mp4`,
    story: {
      summary:
        "An internal inspection workflow tool: upload photos, annotate consistently, and generate professional PDFs stored in the cloud.",
      whoFor: ["Inspectors", "Admins", "Operations"],
      flow: [
        {
          title: "Create an inspection",
          detail:
            "Admins assign inspections; inspectors see their upcoming jobs and required deliverables.",
        },
        {
          title: "Upload photos",
          detail:
            "Photos are uploaded and tied to the correct inspection so organization stays automatic.",
        },
        {
          title: "Annotate consistently",
          detail:
            "Standardized notes/codes help keep reporting consistent and professional.",
        },
        {
          title: "Generate report PDF",
          detail:
            "The platform generates the report and stores it in cloud storage with metadata tracked in the database.",
        },
      ],
      features: [
        "Upcoming inspections and assignment flow",
        "Structured photo upload and labeling",
        "Standardized note system for consistency",
        "PDF report generation + cloud storage links",
      ],
      // architecture: [
      //   "React app for inspector/admin workflows",
      //   "Flask API for auth, uploads, templating, and generation",
      //   "Cloud SQL for inspection/report metadata",
      //   "Cloud Storage for images and final PDFs",
      // ],
      results: [
        "Faster report creation",
        "More consistent output quality across inspectors",
      ],
    },
  },

  {
    key: "asx",
    title: "All Star Xperience",
    subtitle: "Soccer league website & stats hub",
    tag: "Sports Platform",
    audience: "Players, teams, fans - Private Customer",
    desc:
      "A league platform that shows schedules, standings, and top scorers with a clean, modern experience.",
    highlights: [
      "Standings, schedules, and leaderboards",
      "Simple admin-friendly data updates",
      "Mobile-first UI for players",
    ],
    stack: ["React", "Apps Script"],
    media: `${GCS_BASE}/asx.mp4`,
    story: {
      summary:
        "A modern league website powered by Google Sheets: admins update data once, and the website stays current automatically.",
      whoFor: ["Players", "Fans", "League admins"],
      flow: [
        {
          title: "Easy League Registrations",
          detail:
            "Admins can upload new League Registrations and players can easily register.",
        },
        {
          title: "Website fetches live stats",
          detail:
            "Apps Script serves JSON endpoints so the site loads quickly and stays up to date.",
        },
        {
          title: "Fans view standings & leaders",
          detail:
            "Mobile-first pages show standings, schedules, and top scorers with a clean, readable layout.",
        },
        {
          title: "League updates data once",
          detail:
            "Admins update schedules/standings in Google Sheets (fast and familiar).",
        },
        {
          title: "Player photos & profiles",
          detail:
            "Players can be displayed with photos for a more professional and engaging experience.",
        },
      ],
      features: [
        "Standings, schedules, and leaderboards",
        "Fast updates via Google Sheets (no heavy admin panel)",
        "Mobile-first UI",
        "Expandable endpoints for photos and stats",
      ],
      // architecture: [
      //   "Google Sheets as the data source",
      //   "Apps Script web app as a JSON API layer",
      //   "React site for the front-end experience",
      // ],
      results: [
        "Admins can update content quickly",
        "Players and fans get a modern experience on any device",
      ],
    },
  },
];

export function getProjectByKey(key) {
  return projects.find((p) => p.key === key);
}
